// Studio screenshot slideshow for the Landing hero — auto-cycles
// through real captures of two bilingual demo projects:
//   1. [Demo] a_plus_b_squared — hand-written \manimFile{} scenes
//   2. [Demo] a_plus_b_squared_llm — LLM-generated \manim{prompt}
// Both shots show the live Studio shell with the compiled mp4 in
// the preview pane. The LangToggle swaps the per-slide UI lang;
// the slideshow timer crossfades between the two slides every ~6s
// and pauses on hover so visitors can read the .tex source.
//
// Refresh after Studio UI changes:
//   cd backend && conda run -n comfyui --no-capture-output \
//       python scripts/screenshot_landing_studio.py

import { useEffect, useRef, useState } from 'react';
import './StudioMockup.css';
import { useLang, useT } from '../lib/i18n';

interface Slide {
  src: string;
  altKey: string;
  captionKey: string;
}

// Screenshot src is language-specific (the captured UI is zh or en); the
// alt + caption are translation keys resolved via t() at render time.
const SLIDES: Record<'zh' | 'en', Slide[]> = {
  zh: [
    { src: '/landing/studio-zh.png',     altKey: 'mockup.hand.alt', captionKey: 'mockup.hand.caption' },
    { src: '/landing/studio-llm-zh.png', altKey: 'mockup.llm.alt',  captionKey: 'mockup.llm.caption' },
  ],
  en: [
    { src: '/landing/studio-en.png',     altKey: 'mockup.hand.alt', captionKey: 'mockup.hand.caption' },
    { src: '/landing/studio-llm-en.png', altKey: 'mockup.llm.alt',  captionKey: 'mockup.llm.caption' },
  ],
};

const AUTO_ADVANCE_MS = 6000;

export function StudioMockup() {
  const lang = useLang();
  const t = useT();
  const slides = SLIDES[lang];
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  // Clamp the active slide when the lang flip yields fewer slides
  // (currently both langs have 2, but defensive against future skew).
  useEffect(() => {
    if (idx >= slides.length) setIdx(0);
  }, [slides.length, idx]);

  // Auto-advance — restartable via `tick` so manual nav dots can
  // reset the timer.
  const tick = useRef(0);
  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % slides.length);
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(t);
  }, [paused, slides.length, tick.current]);

  function goto(i: number) {
    setIdx(i);
    tick.current += 1;            // restart auto-advance timer
  }

  const current = slides[idx];

  return (
    <div
      className="studio-mockup"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="studio-mockup-stage">
        {/* Horizontal-slide track: all slides sit side by side, the
            track translates by -100% per slide. CSS transition on
            transform gives the smooth carousel motion. */}
        <div
          className="studio-mockup-track"
          style={{ transform: `translateX(-${idx * 100}%)` }}
        >
          {slides.map((s, i) => (
            <img
              key={s.src}
              className="studio-mockup-img"
              src={s.src}
              alt={t(s.altKey)}
              loading={i === 0 ? 'eager' : 'lazy'}
              draggable={false}
            />
          ))}
        </div>
      </div>
      <div className="studio-mockup-controls">
        <div className="studio-mockup-caption">{t(current.captionKey)}</div>
        <div className="studio-mockup-dots" role="tablist" aria-label={t('mockup.aria_slideshow')}>
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={i === idx}
              className={'studio-mockup-dot' + (i === idx ? ' is-active' : '')}
              onClick={() => goto(i)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
